<template>
  <step-container
    v-if="onboardingSepa"
    :has-steps="isOnboarding"
    :is-disabled="!isOnboarding"
    :required-data="requiredData"
    :data="data"
    :on-submit="submit"
    v-bind="$attrs"
    :hide-secondary-actions="hideSecondaryActions"
    :name="'update-company-billing'"
  >
    <billing-data
      v-if="isOnboarding"
      :data="data"
      is-onboarding
      @updateBillingAddress="updateBillingAddress"
    />

    <v-card
      v-else
      flat
    >
      <billing-data
        class="mx-4"
        :data="data"
        @updateBillingAddress="updateBillingAddress"
      />
    </v-card>
  </step-container>
</template>

<script>
import BillingData from '@/components/forms/BillingData'

import ADD_DOCUMENT_TO_ONBOARDING from './queries/AddDocumentToOnboarding.gql'
import COMPANY_DOCUMENTS from './queries/CompanyDocuments.gql'
import UPDATE_COMPANY_BILLING from './queries/UpdateCompanyBilling.gql'
import StepContainer from './StepContainer.vue'

export default {
  components: { StepContainer, BillingData },
  props: {
    companyId: {
      type: String,
      required: true,
    },
    country: {
      type: String,
      required: true,
    },
    customerNumber: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    address: {
      type: Object,
      required: true,
    },
    billing: {
      type: Object,
      required: true,
    },
    isOnboarding: {
      type: Boolean,
      default: false,
    },
    hideSecondaryActions: {
      type: Boolean,
      default: false,
    },
    /** Prefill empty billing contact fields during onboarding (e.g. from ambassador). */
    prefillContact: {
      type: Object,
      default: null,
    },
  },
  data() {
    const billingContact = this.billing?.contact || {}
    const prefill = this.prefillContact || {}
    const pickPrefill = (key, current) => {
      const value = current != null ? String(current).trim() : ''
      if (value) return current
      const fromPrefill = prefill[key]
      return fromPrefill != null && String(fromPrefill).trim() ? fromPrefill : current
    }
    const companyNamePrefill =
      billingContact.companyName?.trim() ||
      prefill.companyName?.trim() ||
      this.billing?.contact?.companyName ||
      ''
    const vatIdPrefill = this.billing?.vatId || prefill.vatId || ''
    const taxNumberPrefill = this.billing?.taxNumber || prefill.taxNumber || ''
    const noVatFromChat =
      this.billing?.chatNoVat === true ||
      prefill.billingNoVat === true ||
      Boolean(taxNumberPrefill && !vatIdPrefill && !this.billing?.vatIdRequestDate)
    return {
      data: {
        onboardingSepa: undefined,
        customerNumber: this.customerNumber,
        hasSeparateBillingAddress: this.billing?.hasSeparateBillingAddress,
        street: this.billing?.address?.street,
        zip: this.billing?.address?.zip,
        city: this.billing?.address?.city,
        companyStreet: this.address.street,
        companyZip: this.address.zip,
        companyCity: this.address.city,
        phone: pickPrefill('phone', billingContact.phone),
        email: pickPrefill('email', billingContact.email),
        companyName: companyNamePrefill || this.name || '',
        companyNameAddition: billingContact.companyNameAddition,
        firstname: pickPrefill('firstname', billingContact.firstname),
        lastname: pickPrefill('lastname', billingContact.lastname),
        vatId: vatIdPrefill,
        taxNumber: taxNumberPrefill,
        vatIdRequestDate: this.billing?.vatIdRequestDate,
        paymentMethod:
          this.billing?.paymentMethod || (this.country === 'ch' ? 'INVOICE' : 'DIRECT_DEBIT'),
        iban: this.billing?.iban,
        bic: this.billing?.bic,
        sepaValidAsOf: this.billing?.sepaValidAsOf,
        sepaInput: undefined,
        vatIdRequested: Boolean(this.billing.vatIdRequestDate) && !noVatFromChat,
        noVatIdAvailable: noVatFromChat,
        companyNameExceeded: false,
        companyCountry: this.country,
      },
    }
  },
  watch: {
    onboardingSepa() {
      this.data.onboardingSepa = this.onboardingSepa
    },
  },
  methods: {
    updateBillingAddress(billingAddress) {
      const { zip, street, city } = billingAddress
      this.data.zip = zip
      this.data.street = street
      this.data.city = city
    },
    requiredData({
      country,
      onboardingSepa,
      vatIdRequested,
      noVatIdAvailable,
      bic,
      iban,
      sepaValidAsOf,
      vatId,
      paymentMethod,
      vatIdRequestDate,
      hasSeparateBillingAddress,
      street,
      zip,
      city,
      phone,
      email,
      firstname,
      lastname,
    }) {
      const vatSatisfied = vatIdRequested
        ? { vatIdRequestDate }
        : noVatIdAvailable
          ? { noVatIdAvailable: true }
          : { vatId }
      return {
        phone,
        email,
        firstname,
        lastname,
        ...vatSatisfied,
        ...(hasSeparateBillingAddress && { street, zip, city }),
        ...(country !== 'ch' && paymentMethod === 'DIRECT_DEBIT' && { iban, bic, sepaValidAsOf }),
        ...(paymentMethod === 'DIRECT_DEBIT' && { onboardingSepa: onboardingSepa.url }),
      }
    },
    async submit({
      sepaInput,
      vatIdRequested,
      noVatIdAvailable,
      sepaValidAsOf,
      vatId,
      taxNumber,
      vatIdRequestDate,
      paymentMethod,
      bic,
      iban,
      hasSeparateBillingAddress,
      companyName,
      companyNameAddition,
      firstname,
      lastname,
      email,
      phone,
      street,
      zip,
      city,
    }) {
      const vatInput = vatIdRequested
        ? { vatIdRequestDate, vatId: undefined, taxNumber: undefined }
        : noVatIdAvailable
          ? {
              vatId: undefined,
              vatIdRequestDate: undefined,
              ...(taxNumber ? { taxNumber } : { taxNumber: undefined }),
            }
          : { vatId, vatIdRequestDate: undefined, taxNumber: undefined }
      await this.$apollo.mutate({
        mutation: UPDATE_COMPANY_BILLING,
        variables: {
          input: {
            companyId: this.companyId,
            paymentMethod,
            iban,
            bic,
            sepaValidAsOf,
            ...vatInput,
            hasSeparateBillingAddress: hasSeparateBillingAddress || false,
            contact: { companyName, companyNameAddition, firstname, lastname, email, phone },
            ...(hasSeparateBillingAddress && { address: { street, zip, city } }),
          },
        },
      })
      if (sepaInput) {
        await this.$apollo.mutate({
          mutation: ADD_DOCUMENT_TO_ONBOARDING,
          variables: {
            input: {
              companyId: this.companyId,
              document: {
                type: 'SEPA',
                file: sepaInput,
              },
            },
          },
          update: (store, { data: { document } }) => {
            const data = store.readQuery({
              query: COMPANY_DOCUMENTS,
              variables: { companyId: this.companyId },
            })
            data.onboardingDocuments = [
              ...data.onboardingDocuments,
              { type: 'SEPA', file: document, __typename: 'ContractDocument' },
            ]
            store.writeQuery({
              query: COMPANY_DOCUMENTS,
              data,
              variables: { companyId: this.companyId },
            })
          },
        })
        this.data.sepaInput = undefined
      }
    },
  },
  apollo: {
    onboardingSepa: {
      query: COMPANY_DOCUMENTS,
      variables() {
        return {
          companyId: this.companyId,
        }
      },
      update: (data) => data.onboardingDocuments.find(({ type }) => type === 'SEPA')?.file || {},
    },
  },
}
</script>
